import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Input,
  InputLabel,
  Checkbox,
  FormControlLabel,
  TextField
} from "@mui/material";

const defaultOpeningTimes = {
  Monday: { open: "09:00", close: "18:00" },
  Tuesday: { open: "09:00", close: "18:00" },
  Wednesday: { open: "09:00", close: "18:00" },
  Thursday: { open: "09:00", close: "18:00" },
  Friday: { open: "09:00", close: "18:00" },
  Saturday: { open: "09:00", close: "13:00" },
  Sunday: { open: "00:00", close: "00:00" },
};

const Settings = ({ open, onClose }) => {
  const [groupData, setGroupData] = useState(null);
  const [editedData, setEditedData] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);

  const [checkboxes, setCheckboxes] = useState({
    scanToShelf: true,
    inventory: true,
    housekeeping: true,
    activities: true,
    communications: true,
    report_sim: false,
    report_expiring: false,
    report_slow: false,
    report_fridge: false,
    send_timesheets: false,   // ⬅️ NEW
  });

  const [timesheetDay, setTimesheetDay] = useState(1); // ⬅️ NEW

  const [openingTimes, setOpeningTimes] = useState(defaultOpeningTimes);
  const [selectedPharmacyIndex, setSelectedPharmacyIndex] = useState(0);
  const [managerEmail, setManagerEmail] = useState("");

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newPharmacyName, setNewPharmacyName] = useState("");
  const [newPharmacyCode, setNewPharmacyCode] = useState("");
  const [addError, setAddError] = useState("");

  useEffect(() => {
    if (open) {
      const savedEmail = localStorage.getItem("manager_email") || "";
      setManagerEmail(savedEmail);

      const organisation_name = localStorage.getItem("organisationName") || "";
      const expireBefore = parseInt(localStorage.getItem("expireBefore")) || 0;
      const slowBefore = parseInt(localStorage.getItem("slowBefore")) || 0;
      const members = JSON.parse(localStorage.getItem("members")) || [];

      const parsed = {
        organisation_name,
        expireBefore,
        slowBefore,
        members,
        email: localStorage.getItem("email") || ""
      };

      setGroupData(parsed);
      setEditedData(JSON.parse(JSON.stringify(parsed)));

      // ⬇️ Load checkboxes
      const storedCheckboxes = {
        scanToShelf: JSON.parse(localStorage.getItem("scanToShelf") || "true"),
        inventory: JSON.parse(localStorage.getItem("inventory") || "true"),
        housekeeping: JSON.parse(localStorage.getItem("housekeeping") || "true"),
        activities: JSON.parse(localStorage.getItem("activities") || "true"),
        communications: JSON.parse(localStorage.getItem("communications") || "true"),
        report_sim: JSON.parse(localStorage.getItem("report_sim") || "false"),
        report_expiring: JSON.parse(localStorage.getItem("report_expiring") || "false"),
        report_slow: JSON.parse(localStorage.getItem("report_slow") || "false"),
        report_fridge: JSON.parse(localStorage.getItem("report_fridge") || "false"),
        send_timesheets: JSON.parse(localStorage.getItem("send_timesheets") || "false"), // ⬅️ NEW
      };

      setCheckboxes(storedCheckboxes);

      // ⬇️ Load timesheet day
      const storedDay = parseInt(localStorage.getItem("timesheet_day")) || 1;
      setTimesheetDay(storedDay);

      if (members.length > 0) {
        const firstTimes = members[0].openingTimes || defaultOpeningTimes;
        setOpeningTimes(firstTimes);
      } else {
        setOpeningTimes(defaultOpeningTimes);
      }

      setSelectedPharmacyIndex(0);
    }
  }, [open]);

  const handleFieldChange = (field, value) => {
    const updated = { ...editedData, [field]: value };
    setEditedData(updated);
    setHasChanges(true);
  };

  const handleCheckboxChange = (field) => {
    const updated = { ...checkboxes, [field]: !checkboxes[field] };
    setCheckboxes(updated);
    setHasChanges(true);
  };

  // ===== Helpers for members UI (added) =====
  const handleSelectPharmacy = (idx) => {
    setSelectedPharmacyIndex(idx);
    const sel = (editedData?.members || [])[idx];
    setOpeningTimes(sel?.openingTimes || defaultOpeningTimes);
  };

  const updateOpeningTime = (day, field, value) => {
    const next = { ...openingTimes, [day]: { ...openingTimes[day], [field]: value } };
    setOpeningTimes(next);
    setHasChanges(true);
  };

  const addPharmacy = () => {
    const name = (newPharmacyName || "").trim();
    const code = (newPharmacyCode || "").trim();
    if (!name) { setAddError("Pharmacy name is required"); return; }

    const nextMembers = Array.isArray(editedData?.members) ? [...editedData.members] : [];
    nextMembers.push({
      pharmacyName: name,
      pharmacyId: code || name,
      openingTimes: defaultOpeningTimes
    });

    const nextEdited = { ...editedData, members: nextMembers };
    setEditedData(nextEdited);
    setNewPharmacyName("");
    setNewPharmacyCode("");
    setAddError("");
    setAddDialogOpen(false);
    setSelectedPharmacyIndex(nextMembers.length - 1);
    setOpeningTimes(defaultOpeningTimes);
    setHasChanges(true);
  };

  const deleteSelectedPharmacy = () => {
    const list = Array.isArray(editedData?.members) ? [...editedData.members] : [];
    if (!list.length) return;
    if (!window.confirm("Remove this pharmacy from the group?")) return;

    list.splice(selectedPharmacyIndex, 1);
    const nextEdited = { ...editedData, members: list };
    setEditedData(nextEdited);

    const nextIndex = Math.max(0, selectedPharmacyIndex - 1);
    setSelectedPharmacyIndex(nextIndex);
    const sel = list[nextIndex];
    setOpeningTimes(sel?.openingTimes || defaultOpeningTimes);
    setHasChanges(true);
  };
  // ===== End helpers =====

  const handleSave = async () => {
    const updated = { ...editedData };
    if (Array.isArray(updated.members) && updated.members.length > 0) {
      updated.members[selectedPharmacyIndex] = {
        ...(updated.members[selectedPharmacyIndex] || {}),
        openingTimes: openingTimes
      };
    }

    const dataToSave = {
      ...updated,
      ...checkboxes,
      timesheet_day: timesheetDay, // ⬅️ include in save payload
      _id: localStorage.getItem("userId"),
      email: groupData?.email || "",
      manager_email: managerEmail,
    };

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(`https://api.pharmacegroup.co.uk/updateGroupSettings`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${token}`
        },
        body: JSON.stringify(dataToSave),
      });

      if (!response.ok) {
        console.error("Failed to update organisation settings");
        return;
      }

      // Persist main fields
      localStorage.setItem("manager_email", managerEmail);
      localStorage.setItem("organisationName", dataToSave.organisation_name);
      localStorage.setItem("expireBefore", dataToSave.expireBefore);
      localStorage.setItem("slowBefore", dataToSave.slowBefore);
      localStorage.setItem("members", JSON.stringify(dataToSave.members));

      // Persist checkboxes
      Object.entries(checkboxes).forEach(([key, value]) => {
        localStorage.setItem(key, JSON.stringify(value));
      });

      // Persist timesheet day
      localStorage.setItem("timesheet_day", timesheetDay);

      setGroupData(editedData);
      setHasChanges(false);
      onClose();
    } catch (err) {
      console.error("Error saving settings:", err);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>Configuration</DialogTitle>
      <DialogContent>
        <div style={{ display: "flex", flexDirection: "row", gap: 24 }}>
          {/* LEFT: Members list */}
          <div style={{ flex: 1 }}>
            <h4>Pharmacies</h4>

            <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
              <Button variant="outlined" size="small" onClick={() => setAddDialogOpen(true)}>Add pharmacy</Button>
              <Button
                variant="outlined"
                color="error"
                size="small"
                onClick={deleteSelectedPharmacy}
                disabled={!editedData?.members?.length}
              >
                Remove selected
              </Button>
            </div>

            <div style={{ border: "1px solid #e5e7eb", borderRadius: 6, maxHeight: 260, overflow: "auto" }}>
              {(editedData?.members?.length ? editedData.members : []).map((m, idx) => (
                <div
                  key={`${m.pharmacyId || m.pharmacyName || idx}-${idx}`}
                  onClick={() => handleSelectPharmacy(idx)}
                  style={{
                    padding: "8px 10px",
                    cursor: "pointer",
                    background: idx === selectedPharmacyIndex ? "#f0f9ff" : "transparent",
                    borderBottom: "1px solid #f3f4f6"
                  }}
                >
                  <div style={{ fontWeight: 600 }}>{m.pharmacyName || m.pharmacyId}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{m.pharmacyId}</div>
                </div>
              ))}
              {(!editedData?.members || editedData.members.length === 0) && (
                <div style={{ padding: 10, color: "#6b7280" }}>No pharmacies yet.</div>
              )}
            </div>
          </div>

          {/* MIDDLE: Opening times for selected pharmacy */}
          <div style={{ flex: 1 }}>
            <h4>Opening times</h4>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {Object.keys(openingTimes).map((day) => (
                <div
                  key={day}
                  style={{ border: "1px solid #e5e7eb", borderRadius: 6, padding: 8 }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 6 }}>{day}</div>
                  <TextField
                    label="Open"
                    size="small"
                    value={openingTimes[day].open}
                    onChange={(e) => updateOpeningTime(day, "open", e.target.value)}
                  />
                  <TextField
                    label="Close"
                    size="small"
                    value={openingTimes[day].close}
                    onChange={(e) => updateOpeningTime(day, "close", e.target.value)}
                    style={{ marginLeft: 8 }}
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Right section: Manager reports */}
          <div style={{ flex: 1, paddingLeft: 20 }}>
            <h4>Manager reports</h4>

            <InputLabel>Manager Email</InputLabel>
            <Input
              type="email"
              fullWidth
              value={managerEmail}
              onChange={(e) => {
                setManagerEmail(e.target.value);
                setHasChanges(true);
              }}
            />

            <div style={{ marginTop: "1rem" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_sim}
                    onChange={() => handleCheckboxChange("report_sim")}
                  />
                }
                label="SIM activity"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_expiring}
                    onChange={() => handleCheckboxChange("report_expiring")}
                  />
                }
                label="Expiring items"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_slow}
                    onChange={() => handleCheckboxChange("report_slow")}
                  />
                }
                label="Slow movers"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={checkboxes.report_fridge}
                    onChange={() => handleCheckboxChange("report_fridge")}
                  />
                }
                label="Fridge checks"
              />

              {/* NEW Timesheets option */}
              <div style={{ display: "flex", alignItems: "center", marginTop: 8, gap: 8 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={checkboxes.send_timesheets}
                      onChange={() => handleCheckboxChange("send_timesheets")}
                    />
                  }
                  label="Send monthly timesheets"
                />
                {/* Day should be visible/editable regardless of send_timesheets */}
                <TextField
                  type="number"
                  label="Timesheet day"
                  size="small"
                  value={timesheetDay}
                  onChange={(e) => {
                    const val = Math.min(31, Math.max(1, parseInt(e.target.value) || 1));
                    setTimesheetDay(val);
                    setHasChanges(true);
                  }}
                  inputProps={{ min: 1, max: 31 }}
                  style={{ width: 120 }}
                />
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} disabled={!hasChanges}>Save changes</Button>
      </DialogActions>

      {/* Add pharmacy dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add pharmacy</DialogTitle>
        <DialogContent>
          <InputLabel>Name</InputLabel>
          <Input fullWidth value={newPharmacyName} onChange={(e) => setNewPharmacyName(e.target.value)} />
          <InputLabel sx={{ mt: 2 }}>Code / ID (optional)</InputLabel>
          <Input fullWidth value={newPharmacyCode} onChange={(e) => setNewPharmacyCode(e.target.value)} />
          {addError && <div style={{ color: "crimson", marginTop: 8 }}>{addError}</div>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={addPharmacy} disabled={!newPharmacyName.trim()}>Add</Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  );
};

export default Settings;
